"use client";

import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  IconButton,
  Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { useNavigate } from "react-router-dom";

interface CrbtProfileResponse {
  crbtProfile: {
    toneSetting: ToneSetting[];
  };
}


interface ToneSetting {
    artistName: string;
  date: string | null;
  days: string;
  endHr: string;
  fmsisdn: string | null;
  friendName: string | null;
  groupId: number;
  groupName: string | null;
  isPurchased: boolean;
  rbtCode: number;
  rbtName: string;
  settingFor: string;
  stHr: string;
}

const ToneSettingsPage: React.FC = () => {
  const [toneSetting, setToneSetting] = useState<ToneSetting[]>([]);
  const [alertMsg, setAlertMsg] = useState<string | null>(null);

    const { countryCode, msisdn } = useSelector(
                (state: RootState) => state.phone
              );

              const { subType, planId } = useSelector(
                (state: RootState) => state.profile
              );

              const navigate = useNavigate();

  useEffect(() => {
    const fetchToneSettings = async () => {
      try {
        const { data } = await axios.get<CrbtProfileResponse>(
  `${process.env.REACT_APP_API_URL}/Profile/ToneSetting?msisdn=${countryCode+msisdn}&service=${process.env.REACT_APP_SERVICE}&interface=${process.env.REACT_APP_INTERFACES}&langId=${process.env.REACT_APP_LANGID}`
);

setToneSetting(data.crbtProfile.toneSetting);

        setAlertMsg("Success! Settings loaded.");
      } catch (error: any) {
        console.error(error);
        setAlertMsg("Error loading tone settings");
      }
    };

    fetchToneSettings();
  }, []);

  const formatTime = (value: number) => {
    if (value === 2500) return "00:00";
    if (value < 1000) return `${String(value).charAt(0)}:00`;
    return `${String(value).substring(0, 2)}:00`;
  };

const getDays = (days: string) => {
  if (!days) return ""; 

  const clean = days.trim();

  if (clean === "8") return "8 Days";

  const filtered = clean.replace(/[^1-7]/g, "");

  if (filtered.length === 0) return ""; 

  const dayMap: Record<string, string> = {
    "1": "Mo",
    "2": "Tu",
    "3": "We",
    "4": "Th",
    "5": "Fr",
    "6": "Sa",
    "7": "Su",
  };

  return filtered
    .split("")
    .map((d) => dayMap[d])
    .join(", ");
};


const handleDelete = async (setting: ToneSetting) => {
  if (!window.confirm(`Are you sure you want to delete ${setting.rbtName}?`)) {
    return;
  }

  try {
    const params = new URLSearchParams({
      msisdn: countryCode + msisdn,
      service: process.env.REACT_APP_SERVICE || "CRBT",
      interface: process.env.REACT_APP_INTERFACES || "W",
      langId: process.env.REACT_APP_LANGID || "1",
      planId: String(planId), 
      rbtCode: String(setting.rbtCode),
      subType: String(subType),
      settingFor: setting.settingFor,
      fmsisdn: setting.fmsisdn ?? "NA",
      date: setting.date ?? "",
      groupId: String(setting.groupId),
      stHr: setting.stHr,
      endHr: setting.endHr,
      days: setting.days,
    });


    const url = `${process.env.REACT_APP_API_URL}/Purchase/DeleteRBTSetting?${params.toString()}`;

    const { data } = await axios.delete(url);

    if(data.resCode === 233){
      alert(data.resMessage);
    } else {
      alert(data.resMessage || "Failed to delete tone setting.");
    }

    setToneSetting((prev) =>
      prev.filter((item) => item.rbtCode !== setting.rbtCode)
    );

    setAlertMsg("Tone deleted successfully!");
  } catch (error) {
    console.error(error);
    setAlertMsg("Failed to delete tone.");
  }
};


  return (
    <Box sx={{ p: 3 }}>
      {/* {alertMsg && (
        <Alert
          severity={alertMsg.startsWith("Success") ? "success" : "error"}
          sx={{ mb: 2, textAlign: "center" }}
          onClose={() => setAlertMsg(null)}
        >
          {alertMsg}
        </Alert>
      )} */}

      <Typography variant="h5" align="center" gutterBottom color="#000">
        Current Tones Settings
      </Typography>

      <TableContainer component={Paper}>
        <Table sx={{ border: "1px solid #ccc" }}>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#06a5dd",
                "& th": {
                  color: "#fff",
                  fontWeight: "bold",
                  textAlign: "center",
                  borderRight: "1px solid #fff",
                  fontSize: 14,
                  padding: "8px 0",
                },
                "& th:last-child": { borderRight: "none" },
              }}
            >
              <TableCell>Caller</TableCell>
              <TableCell>Tone Title</TableCell>
              <TableCell>Timings</TableCell>
              <TableCell>Edit</TableCell>
              <TableCell>Del</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {toneSetting?.map((element:any, index) => {
              const timeRange = `${formatTime(element?.stHr)} - ${formatTime(
                element.endHr
              )}`;


              return (
                <TableRow key={index}>
                  <TableCell sx={{ color: "#0085ca" }} align="center">
                    {element.settingFor === "FRIEND" && element.friendName}
                    {element.settingFor === "GROUP" && element.groupName}
                    {element.settingFor === "DATE" &&
                      element.date?.substring(0, 10)}
                    <Typography fontSize={12}>{element.settingFor}</Typography>
                  </TableCell>

                  <TableCell sx={{ color: "#0085ca" }} align="center">
                    {element.rbtName}
                  </TableCell>

                  <TableCell align="center">
                    <Typography fontSize={14} color="text.primary">
                      {timeRange}
                    </Typography>
                    {element.settingFor !== "DATE" && (
                      <Typography fontSize={12} color="error">
                        {getDays(element.days)}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => navigate(`/flowtone/changeToneSetting?index=${index+1}`, {state: element})}
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>

                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(element)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ToneSettingsPage;
